<template>
	<div
		class="g-header-carousel-image-preview"
		:class="{
			'g-header-carousel-image-preview--h5': h5,
			'g-header-carousel-image-preview--compact': compact,
		}"
		:style="rootStyle"
	>
		<div
			ref="carousel"
			class="g-header-carousel-image-preview__viewport"
			@touchstart.passive="onTouchStart"
			@touchmove="onTouchMove"
			@touchend="onTouchEnd"
			@touchcancel="onTouchEnd"
		>
			<div
				class="g-header-carousel-image-preview__track"
				:class="{ 'is-dragging': isTouchDragging }"
				:style="trackStyle"
			>
				<div
					v-for="(slide, index) in slides"
					:key="slide.id"
					class="g-header-carousel-image-preview__slide"
					@click="onSlideClick(slide, index)"
				>
					<img
						v-if="getImageUrl(slide)"
						:src="getImageUrl(slide)"
						class="g-header-carousel-image-preview__bg"
						alt=""
					/>
				</div>
			</div>
		</div>

		<div
			v-if="slides.length > 1"
			class="g-header-carousel-image-preview__dots"
		>
			<span
				v-for="(slide, index) in slides"
				:key="`dot-${slide.id}`"
				class="g-header-carousel-image-preview__dot"
				:class="{ 'is-active': index === activeIndex }"
				:style="getDotStyle(index)"
				@click.stop="setActiveIndex(index)"
			></span>
		</div>
	</div>
</template>

<script>
	import { navigateJumpLink } from '../utils/jumpLink.js';
	import { getHeaderCarouselImageUrl, normalizeHeaderCarouselData } from '../utils/headerCarousel.js';

	const SWIPE_MIN_PX = 40;
	const SWIPE_RATIO = 0.18;

	export default {
		name: 'GHeaderCarouselImagePreview',
		props: {
			data: {
				type: Object,
				default: () => ({}),
			},
			h5: {
				type: Boolean,
				default: false,
			},
			compact: {
				type: Boolean,
				default: false,
			},
			interactive: {
				type: Boolean,
				default: true,
			},
		},
		emits: ['update:activeIndex'],
		data() {
			return {
				activeIndex: 0,
				touchStartX: 0,
				touchStartY: 0,
				isTouchDragging: false,
				dragOffset: 0,
				carouselWidth: 0,
				autoplayTimer: null,
			};
		},
		computed: {
			normalizedData() {
				return normalizeHeaderCarouselData(this.data);
			},
			slides() {
				return this.normalizedData.items;
			},
			rootStyle() {
				const height = this.normalizedData.height;
				const radius = this.normalizedData.borderRadius;
				const unit = this.h5 ? 'rem' : 'px';
				const heightValue = this.h5 ? height / 100 : height;
				const radiusValue = this.h5 ? radius / 50 : radius;
				return {
					'--g-header-carousel-radius': `${radiusValue}${unit}`,
					'--g-header-carousel-height': `${heightValue}${unit}`,
					'--g-header-carousel-dot-color': this.normalizedData.dotColor,
					'--g-header-carousel-dot-active-color': this.normalizedData.dotActiveColor,
					'--g-header-carousel-ratio': this.h5 ? `${538} / ${height}` : null,
				};
			},
			canSwipe() {
				return this.interactive && this.slides.length > 1;
			},
			trackStyle() {
				const baseOffset = this.activeIndex * 100;
				if (!this.isTouchDragging || !this.carouselWidth) {
					return {
						transform: `translateX(-${baseOffset}%)`,
					};
				}
				return {
					transform: `translateX(calc(-${baseOffset}% + ${this.dragOffset}px))`,
				};
			},
		},
		watch: {
			data: {
				handler() {
					this.activeIndex = 0;
					this.resetTouchState();
					this.syncIndexBounds();
					this.restartAutoplay();
				},
			},
			slides: {
				handler() {
					this.syncIndexBounds();
				},
			},
			'normalizedData.autoplay'() {
				this.restartAutoplay();
			},
			'normalizedData.autoplayInterval'() {
				this.restartAutoplay();
			},
		},
		mounted() {
			this.syncIndexBounds();
			this.restartAutoplay();
		},
		beforeUnmount() {
			this.stopAutoplay();
		},
		methods: {
			getImageUrl(slide) {
				return getHeaderCarouselImageUrl(slide?.image);
			},
			getDotStyle(index) {
				const isActive = index === this.activeIndex;
				if (this.h5) {
					return {
						background: isActive
							? this.normalizedData.dotActiveColor
							: this.normalizedData.dotColor,
						width: isActive ? '0.28rem' : '0.12rem',
						height: '0.12rem',
						borderRadius: isActive ? '0.06rem' : '50%',
					};
				}
				return {
					background: isActive
						? this.normalizedData.dotActiveColor
						: this.normalizedData.dotColor,
					width: isActive ? '14px' : '6px',
					height: '6px',
					borderRadius: isActive ? '3px' : '50%',
				};
			},
			setActiveIndex(index) {
				if (!this.interactive) return;
				const max = Math.max(0, this.slides.length - 1);
				this.activeIndex = Math.min(Math.max(0, index), max);
				this.$emit('update:activeIndex', this.activeIndex);
				this.restartAutoplay();
			},
			syncIndexBounds() {
				const max = Math.max(0, this.slides.length - 1);
				if (this.activeIndex > max) {
					this.activeIndex = max;
					this.$emit('update:activeIndex', max);
				}
			},
			onSlideClick(slide, index) {
				if (!this.h5 || !slide?.jumpLink?.type) return;
				navigateJumpLink(slide.jumpLink, {
					apiRequest: this.$apiRequest,
				});
				this.setActiveIndex(index);
			},
			getCarouselWidth() {
				return this.$refs.carousel?.offsetWidth || 0;
			},
			onTouchStart(event) {
				if (!this.canSwipe) return;
				this.stopAutoplay();
				const touch = event.touches[0];
				this.touchStartX = touch.clientX;
				this.touchStartY = touch.clientY;
				this.isTouchDragging = false;
				this.dragOffset = 0;
				this.carouselWidth = this.getCarouselWidth();
			},
			onTouchMove(event) {
				if (!this.canSwipe) return;
				const touch = event.touches[0];
				const deltaX = touch.clientX - this.touchStartX;
				const deltaY = touch.clientY - this.touchStartY;
				if (!this.isTouchDragging) {
					if (Math.abs(deltaX) < 8) return;
					if (Math.abs(deltaX) <= Math.abs(deltaY)) return;
					this.isTouchDragging = true;
				}
				event.preventDefault();
				let offset = deltaX;
				if (this.activeIndex === 0 && offset > 0) {
					offset *= 0.35;
				} else if (this.activeIndex === this.slides.length - 1 && offset < 0) {
					offset *= 0.35;
				}
				this.dragOffset = offset;
			},
			onTouchEnd() {
				if (!this.canSwipe) {
					this.resetTouchState();
					this.restartAutoplay();
					return;
				}
				if (this.isTouchDragging) {
					const threshold = Math.max(
						SWIPE_MIN_PX,
						this.carouselWidth * SWIPE_RATIO,
					);
					if (this.dragOffset < -threshold && this.activeIndex < this.slides.length - 1) {
						this.setActiveIndex(this.activeIndex + 1);
					} else if (this.dragOffset > threshold && this.activeIndex > 0) {
						this.setActiveIndex(this.activeIndex - 1);
					}
				}
				this.resetTouchState();
				this.restartAutoplay();
			},
			resetTouchState() {
				this.isTouchDragging = false;
				this.dragOffset = 0;
			},
			stopAutoplay() {
				if (this.autoplayTimer) {
					clearInterval(this.autoplayTimer);
					this.autoplayTimer = null;
				}
			},
			restartAutoplay() {
				this.stopAutoplay();
				if (!this.interactive || !this.normalizedData.autoplay || this.slides.length <= 1) {
					return;
				}
				this.autoplayTimer = setInterval(() => {
					const next = (this.activeIndex + 1) % this.slides.length;
					this.setActiveIndex(next);
				}, this.normalizedData.autoplayInterval);
			},
		},
	};
</script>

<style scoped lang="less">
	.g-header-carousel-image-preview {
		position: relative;
		width: 100%;
		border-radius: var(--g-header-carousel-radius, 12px);
		overflow: hidden;
	}

	.g-header-carousel-image-preview__viewport {
		position: relative;
		height: var(--g-header-carousel-height, 180px);
		overflow: hidden;
		touch-action: pan-x pinch-zoom;
	}

	.g-header-carousel-image-preview__track {
		display: flex;
		height: 100%;
		transition: transform 0.25s ease;

		&.is-dragging {
			transition: none;
		}
	}

	.g-header-carousel-image-preview__slide {
		position: relative;
		flex: 0 0 100%;
		min-width: 0;
		height: 100%;
		overflow: hidden;
	}

	.g-header-carousel-image-preview__bg {
		position: absolute;
		inset: 0;
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	.g-header-carousel-image-preview__dots {
		position: absolute;
		left: 0;
		right: 0;
		bottom: 10px;
		z-index: 2;
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 5px;
		pointer-events: auto;
	}

	.g-header-carousel-image-preview__dot {
		display: inline-block;
		flex-shrink: 0;
		vertical-align: middle;
		cursor: pointer;
		transition: background 0.2s ease;
	}

	.g-header-carousel-image-preview--h5 {
		aspect-ratio: var(--g-header-carousel-ratio, 538 / 360);
		margin-top: 0.3rem;

		.g-header-carousel-image-preview__viewport {
			position: absolute;
			inset: 0;
			height: 100%;
		}

		.g-header-carousel-image-preview__dots {
			bottom: 0.2rem;
			gap: 0.1rem;
		}
	}

	.g-header-carousel-image-preview--compact {
		.g-header-carousel-image-preview__viewport {
			height: 192px;
		}
	}
</style>
